import { AvatarStack, Box, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectTasksDataTableDocument,
  ProjectionKind,
  useProjectTasksDataTable,
} from 'generated/resources/datatable.graphql'
import { ProjectTaskAnalyticsDocument } from 'generated/resources/graphql'
import compact from 'lodash/compact'
import moment from 'moment'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import TaskStatus from 'modules/tasks/components/TaskStatus'
import { TaskIdentifier } from 'modules/tasks/lib/task'

type TasksDatatableProjection = Parameters<typeof useProjectTasksDataTable>[0][number]

const ProjectTaskName = (): TasksDatatableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'name',
  title: i18n.t('project_management.table.tasks.headers.name'),
  render: ({ task }) => (
    <Box minWidth="s200">
      <Text.Normal stroked={!!task?.completedAt} overflowWrap>
        {task?.name}
      </Text.Normal>
    </Box>
  ),
  template: '{{task.name}}',
  colSpan: 3,
})

const ProjectTaskSubproject = (): TasksDatatableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'subproject',
  title: i18n.t('project_management.table.tasks.headers.subproject'),
  render: ({ subproject }) => (
    <Box minWidth="s200">
      <Text.Normal overflowWrap>{subproject?.name}</Text.Normal>
    </Box>
  ),
  template: '{{subproject.name}}',
  colSpan: 3,
})

const ProjectTaskAssignee = (): TasksDatatableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'assignee',
  title: i18n.t('project_management.table.tasks.headers.assignees'),
  render: ({ task }) => {
    const avatars =
      task?.assigneesConnection.edges?.map(({ node }) => {
        return {
          label: node.fullName,
          url: buildUrl(node.avatar ?? ''),
        }
      }) || []
    const leftover = (task?.assigneesConnection.totalCount ?? 0) - 2

    return (
      <Box flexDirection="row" alignItems="flexStart" justifyContent="flexStart">
        <AvatarStack
          size="s32"
          leftover={leftover}
          overlap="s8"
          backgroundColor="champagne1000"
          textSize="200"
          textColor="grey000White"
          avatars={avatars}
        />
      </Box>
    )
  },
  template: `{% for edge in task.assigneesConnection.edges -%}
  {{ edge.node.fullName }}
  {%- unless forloop.last -%}, {%- endunless -%}
{%- endfor %}`,
  colSpan: 3,
})

const ProjectTaskStatus = (projectId: number): TasksDatatableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'status',
  title: i18n.t('project_management.table.tasks.headers.status'),
  render: ({ id: projectTaskId, task }) => (
    <Box onClick={(event: MouseEvent) => event.preventDefault()} minWidth="s140">
      <TaskStatus
        taskId={Number(task?.id)}
        status={task?.status}
        identifier={TaskIdentifier.Project}
        refetchQueries={[
          {
            query: ProjectTasksDataTableDocument,
            variables: { ids: [projectTaskId], projectIds: [projectId] },
          },
          {
            query: ProjectTaskAnalyticsDocument,
            variables: { projectIds: [projectId], completed: true, overdue: true },
          },
        ]}
      />
    </Box>
  ),
  template: '{{task.status}}',
  colSpan: 2,
})

const ProjectTaskDates = (): TasksDatatableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'dates',
  title: i18n.t('project_management.table.tasks.headers.dates'),
  render: ({ task }) => {
    const startsOnLabel = task?.startsOn ? dateBackendToUI(task?.startsOn) : '-'
    const dueOnLabel = task?.dueOn ? dateBackendToUI(task?.dueOn) : '-'
    return (
      <Box minWidth="s200" flexDirection="row" gap="s12" alignItems="center">
        <Box width="s80" textAlign="center" justifyContent="center" alignItems="center">
          <Text.Normal size="200">{startsOnLabel}</Text.Normal>
        </Box>
        <Icon.Small icon="arrow-right" color="grey800" />
        <Box width="s80" textAlign="center" justifyContent="center" alignItems="center">
          <Text.Normal size="200">{dueOnLabel}</Text.Normal>
        </Box>
      </Box>
    )
  },
  template: '{{task.startsOn}} - {{task.dueOn}}',
  colSpan: 3,
})

const ProjectTaskCompletedAt = (): TasksDatatableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'completedAt',
  title: i18n.t('project_management.table.tasks.headers.completed_at'),
  render: ({ task }) => {
    return (
      <Box minWidth="s120">
        <Text.Normal overflowWrap>
          {task?.completedAt ? moment(task?.completedAt).format('L') : '-'}
        </Text.Normal>
      </Box>
    )
  },
  template: '{{task.completedAt | date: "%Y-%m-%d" }}',
  colSpan: 1,
})

export const projectTasksProjections = (projectId: number): TasksDatatableProjection[] =>
  compact([
    ProjectTaskName(),
    ProjectTaskSubproject(),
    ProjectTaskAssignee(),
    ProjectTaskStatus(projectId),
    ProjectTaskDates(),
    ProjectTaskCompletedAt(),
  ])
